import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS } from "../theme";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Icon from "../components/ui/Icon";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import Modal from "../components/ui/Modal";

const SUPORTE_EMAIL = "contato@pontozipar.com.br";
const SUPORTE_WPP = "(31) 3490-3317";
const SUPORTE_WPP_LINK = "https://wa.me/553134903317";

const BASE_URL = process.env.REACT_APP_API_URL || "";

export function logoUrl(empresa) {
  return empresa?.logo_path ? `${BASE_URL}/api/empresas/${empresa.id}/logo?v=${empresa.logo_path}` : null;
}

// Endereço salva sozinho ao sair do campo (sem botão "Salvar" separado) —
// mesmo padrão dos outros campos únicos do sistema.
function CampoEndereco({ empresa, podeEditar, onSalvar }) {
  const [valor, setValor] = useState(empresa.endereco || "");
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { setValor(empresa.endereco || ""); }, [empresa.endereco]);

  if (!podeEditar) {
    return <span>{empresa.endereco || "Endereço não informado"}</span>;
  }

  return (
    <input
      value={valor}
      placeholder="Informe o endereço da empresa"
      onChange={(e) => setValor(e.target.value)}
      onBlur={() => {
        if (valor === (empresa.endereco || "")) return;
        setSalvando(true);
        onSalvar(empresa.id, valor).finally(() => setSalvando(false));
      }}
      style={{
        border: "none", borderBottom: `1px dashed ${COLORS.border}`, background: "transparent",
        fontSize: 13, color: COLORS.textMid, padding: "2px 0", width: "100%", outline: "none",
        opacity: salvando ? 0.6 : 1,
      }}
    />
  );
}

// Dados de nome/CNPJ vêm do cadastro feito pelo admin (painel próprio, fora
// do tenant) — aqui é leitura + edição só do que é operacional (logo, endereço).
export default function Empresas() {
  const { token, usuario } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviandoId, setEnviandoId] = useState(null);
  const [erro, setErro] = useState("");
  const [suporteAberto, setSuporteAberto] = useState(false);
  const inputsRef = useRef({});

  const podeEditar = ["gestor", "admin_empresa"].includes(usuario?.perfil);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/empresas", undefined, token);
      setEmpresas(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const enviarLogo = async (empresaId, arquivo) => {
    if (!arquivo) return;
    setErro("");
    setEnviandoId(empresaId);
    try {
      const form = new FormData();
      form.append("logo", arquivo);
      await apiFetch("POST", `/api/empresas/${empresaId}/logo`, form, token);
      await carregar();
    } catch (e) {
      setErro(e.message || "Não foi possível enviar a logo");
    } finally {
      setEnviandoId(null);
    }
  };

  const removerLogo = async (empresa) => {
    if (!window.confirm(`Excluir a logo de ${empresa.nome}? A empresa fica sem logo até você enviar outra.`)) return;
    setErro("");
    setEnviandoId(empresa.id);
    try {
      await apiFetch("DELETE", `/api/empresas/${empresa.id}/logo`, undefined, token);
      await carregar();
    } catch (e) {
      setErro(e.message || "Não foi possível excluir a logo");
    } finally {
      setEnviandoId(null);
    }
  };

  const salvarEndereco = async (empresaId, endereco) => {
    try {
      await apiFetch("PUT", `/api/empresas/${empresaId}`, { endereco }, token);
      await carregar();
    } catch (e) {
      setErro(e.message || "Não foi possível salvar o endereço");
    }
  };

  if (loading) return <EstadoCarregando />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: 0 }}>Empresas</h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, marginTop: 4 }}>
          Todas as empresas (CNPJs) do grupo. Nome e CNPJ vêm do cadastro feito pela equipe do
          PontoZipar; logo e endereço dá pra ajustar por aqui. Qualquer dúvida,{" "}
          <button
            onClick={() => setSuporteAberto(true)}
            style={{ background: "none", border: "none", padding: 0, color: COLORS.primary, fontWeight: 600, fontSize: 13.5, cursor: "pointer", textDecoration: "underline" }}
          >
            contatar o suporte
          </button>
          .
        </p>
      </div>

      <Modal open={suporteAberto} onClose={() => setSuporteAberto(false)} title="Contatar suporte" width={360}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <a
            href={`mailto:${SUPORTE_EMAIL}`}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${COLORS.border}`, color: COLORS.text, textDecoration: "none", fontSize: 13.5, fontWeight: 600 }}
          >
            <Icon name="mail" size={17} color={COLORS.primary} />
            {SUPORTE_EMAIL}
          </a>
          <a
            href={SUPORTE_WPP_LINK}
            target="_blank"
            rel="noreferrer"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: "#25D366", color: "#fff", textDecoration: "none", fontSize: 13.5, fontWeight: 700 }}
          >
            <Icon name="messageCircle" size={17} color="#fff" />
            WhatsApp {SUPORTE_WPP}
          </a>
        </div>
      </Modal>

      {erro && (
        <div style={{ color: COLORS.danger, fontSize: 13.5, background: COLORS.dangerBg, padding: "10px 14px", borderRadius: 10 }}>
          {erro}
        </div>
      )}

      {empresas.length === 0 ? (
        <EstadoVazio icon="briefcase" texto="Nenhuma empresa cadastrada" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {empresas.map((empresa) => (
            <Card key={empresa.id} style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: 18 }}>
              <div
                style={{
                  width: 56, height: 56, borderRadius: 12, background: COLORS.gray,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden",
                }}
              >
                {logoUrl(empresa) ? (
                  <img src={logoUrl(empresa)} alt={empresa.nome} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (
                  <Icon name="briefcase" size={22} color={COLORS.textLight} />
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{empresa.nome}</span>
                  <Badge tone={empresa.ativo ? "success" : "danger"}>{empresa.ativo ? "Ativa" : "Inativa"}</Badge>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name="fileText" size={13} color={COLORS.textLight} />
                    <span style={{ fontSize: 13, color: COLORS.textMid }}>{empresa.cnpj || "CNPJ não informado"}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name="mapPin" size={13} color={COLORS.textLight} style={{ flexShrink: 0 }} />
                    <CampoEndereco empresa={empresa} podeEditar={podeEditar} onSalvar={salvarEndereco} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name="users" size={13} color={COLORS.textLight} />
                    <span style={{ fontSize: 13, color: COLORS.textMid }}>
                      {empresa.funcionarios_count} funcionário{empresa.funcionarios_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </div>

              {podeEditar && (
                <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch" }}>
                  <input
                    ref={(el) => { inputsRef.current[empresa.id] = el; }}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => enviarLogo(empresa.id, e.target.files[0])}
                  />
                  <button
                    onClick={() => inputsRef.current[empresa.id]?.click()}
                    disabled={enviandoId === empresa.id}
                    style={{
                      background: COLORS.primaryBg, color: COLORS.primary, border: "none", borderRadius: 9,
                      padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    {enviandoId === empresa.id ? "Enviando..." : empresa.logo_path ? "Trocar logo" : "Enviar logo"}
                  </button>
                  {empresa.logo_path && (
                    <button
                      onClick={() => removerLogo(empresa)}
                      disabled={enviandoId === empresa.id}
                      style={{
                        background: "transparent", color: COLORS.danger, border: "none", borderRadius: 9,
                        padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      Excluir logo
                    </button>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
